import { useRequiredContext } from "@redotech/react-util/context";
import {
  SpecificDateOperator,
  TimeBasedMode,
  type TimeBasedCondition as TimeBasedConditionType,
} from "@redotech/redo-flows-common/rules-v3/schemas";
import { RuleConditionType } from "@redotech/redo-flows-common/rules/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Link } from "@redotech/redo-web/link";
import {
  TimezoneSelector,
  WeeklyScheduleEditor,
  type WeeklyScheduleValue,
} from "@redotech/redo-web/schedule/weekly-schedule-editor";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";

const modeOptions = () => [
  {
    id: TimeBasedMode.BUSINESS_HOURS,
    type: "text" as const,
    text: t("Business hours"),
    value: TimeBasedMode.BUSINESS_HOURS,
  },
  {
    id: TimeBasedMode.WEEKLY_SCHEDULE,
    type: "text" as const,
    text: t("Weekly schedule"),
    value: TimeBasedMode.WEEKLY_SCHEDULE,
  },
  {
    id: TimeBasedMode.SPECIFIC_DATE,
    type: "text" as const,
    text: t("Specific date"),
    value: TimeBasedMode.SPECIFIC_DATE,
  },
];

const specificDateOperatorOptions = () => [
  {
    id: SpecificDateOperator.BEFORE,
    type: "text" as const,
    text: t("Before", { context: "time is earlier than the value" }),
    value: SpecificDateOperator.BEFORE,
  },
  {
    id: SpecificDateOperator.AFTER,
    type: "text" as const,
    text: t("After", { context: "time is later than the value" }),
    value: SpecificDateOperator.AFTER,
  },
  {
    id: SpecificDateOperator.BETWEEN,
    type: "text" as const,
    text: t("Between", { context: "comparison operator for a range" }),
    value: SpecificDateOperator.BETWEEN,
  },
];

export const TimeBasedCondition = memo(function TimeBasedCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TimeBasedConditionType;
  onChange: (updated: TimeBasedConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  // Lock the editor when no top-level fields are bound; only `mode` is gated per-field.
  const isEditable = (f: string) =>
    editableFields === undefined || editableFields.has(f);
  const anyEditable =
    editableFields === undefined ||
    ["mode", "schedule", "timezone", "dateOperator", "dateTime"].some((f) =>
      editableFields.has(f),
    );
  const modeEditable = isEditable("mode");

  const handleModeChange = useCallback(
    (mode: TimeBasedMode) => {
      switch (mode) {
        case TimeBasedMode.BUSINESS_HOURS:
          onChange({
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.BUSINESS_HOURS,
          });
          break;
        case TimeBasedMode.WEEKLY_SCHEDULE:
          onChange({
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.WEEKLY_SCHEDULE,
          });
          break;
        case TimeBasedMode.SPECIFIC_DATE:
          onChange({
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.SPECIFIC_DATE,
            dateOperator: SpecificDateOperator.AFTER,
          });
          break;
      }
    },
    [onChange],
  );

  const selectedMode = useMemo(
    () => modeOptions().find((opt) => opt.value === condition.mode),
    [condition.mode],
  );

  return (
    <Flex
      dir="column"
      gap="md"
      /* design-allow: disable pointer events and fade when the rule binds no editable fields */
      style={anyEditable ? undefined : { pointerEvents: "none", opacity: 0.6 }}
      w="full"
    >
      <RedoSingleSelectDropdownInput
        disabled={!modeEditable}
        options={modeOptions()}
        optionSelected={({ item }) => {
          if (!modeEditable) {
            return;
          }
          handleModeChange(item.value);
        }}
        placeholder={t("Select mode")}
        selectedItem={selectedMode}
      />
      {condition.mode === TimeBasedMode.BUSINESS_HOURS && (
        <BusinessHoursSection />
      )}
      {condition.mode === TimeBasedMode.WEEKLY_SCHEDULE && (
        <WeeklyScheduleSection condition={condition} onChange={onChange} />
      )}
      {condition.mode === TimeBasedMode.SPECIFIC_DATE && (
        <SpecificDateSection condition={condition} onChange={onChange} />
      )}
    </Flex>
  );
});

const BusinessHoursSection = memo(function BusinessHoursSection() {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const customerWidgetSupportLink = `/stores/${team._id}/settings/customer-widget?tab=support`;

  return (
    <Text fontSize="sm" textColor="secondary">
      {t("Matches when a message is received outside of business hours.")}{" "}
      <Link to={customerWidgetSupportLink}>
        {t("Configure business hours")}
      </Link>
    </Text>
  );
});

export const WeeklyScheduleSection = memo(function WeeklyScheduleSection({
  condition,
  onChange,
}: {
  condition: Extract<TimeBasedConditionType, { mode: "weekly_schedule" }>;
  onChange: (updated: TimeBasedConditionType) => void;
}) {
  const schedule = useMemo(
    () => (condition.schedule ?? {}) as WeeklyScheduleValue,
    [condition.schedule],
  );

  const handleTimezoneChange = useCallback(
    (tz: string) => {
      onChange({ ...condition, timezone: tz });
    },
    [condition, onChange],
  );

  const handleScheduleChange = useCallback(
    (value: WeeklyScheduleValue) => {
      onChange({ ...condition, schedule: value });
    },
    [condition, onChange],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <TimezoneSelector
        onChange={handleTimezoneChange}
        timezone={condition.timezone}
      />
      <WeeklyScheduleEditor onChange={handleScheduleChange} value={schedule} />
    </Flex>
  );
});

const SpecificDateSection = memo(function SpecificDateSection({
  condition,
  onChange,
}: {
  condition: Extract<TimeBasedConditionType, { mode: "specific_date" }>;
  onChange: (updated: TimeBasedConditionType) => void;
}) {
  const { t } = useTranslation();
  const selectedOperator = useMemo(
    () =>
      specificDateOperatorOptions().find(
        (opt) => opt.value === condition.dateOperator,
      ),
    [condition.dateOperator],
  );

  const parseDate = (
    dateStr: string | undefined,
  ): Temporal.PlainDate | undefined => {
    if (!dateStr) {
      return undefined;
    }
    try {
      return Temporal.PlainDate.from(dateStr.split("T")[0] ?? dateStr);
    } catch {
      return undefined;
    }
  };

  const parseTime = (dateStr: string | undefined): string => {
    if (!dateStr) {
      return "00:00";
    }
    const timePart = dateStr.split("T")[1];
    return timePart?.slice(0, 5) ?? "00:00";
  };

  const buildDateTime = (date: string, time: string): string => {
    return `${date}T${time}`;
  };

  return (
    <Flex dir="column" gap="md" w="full">
      <TimezoneSelector
        onChange={(tz) => onChange({ ...condition, timezone: tz })}
        timezone={condition.timezone}
      />
      <RedoSingleSelectDropdownInput
        options={specificDateOperatorOptions()}
        optionSelected={({ item }) =>
          onChange({ ...condition, dateOperator: item.value })
        }
        selectedItem={selectedOperator}
      />
      {(condition.dateOperator === SpecificDateOperator.BEFORE ||
        condition.dateOperator === SpecificDateOperator.AFTER) && (
        <Flex align="center" gap="sm">
          <RedoSingleDatePicker
            buttonSize="sm"
            date={parseDate(condition.dateTime)}
            setDate={(date: Temporal.PlainDate) =>
              onChange({
                ...condition,
                dateTime: buildDateTime(
                  date.toString(),
                  parseTime(condition.dateTime),
                ),
              })
            }
          />
          <RedoTextInput
            inputTypeHint="time"
            placeholder="00:00"
            setValue={(time: string) =>
              onChange({
                ...condition,
                dateTime: buildDateTime(
                  parseDate(condition.dateTime)?.toString() ??
                    Temporal.Now.plainDateISO().toString(),
                  time,
                ),
              })
            }
            size="sm"
            value={parseTime(condition.dateTime)}
          />
        </Flex>
      )}
      {condition.dateOperator === SpecificDateOperator.BETWEEN && (
        <Flex align="center" gap="sm" wrap="wrap">
          <Flex align="center" gap="sm">
            <RedoSingleDatePicker
              buttonSize="sm"
              date={parseDate(condition.startDateTime)}
              setDate={(date: Temporal.PlainDate) =>
                onChange({
                  ...condition,
                  startDateTime: buildDateTime(
                    date.toString(),
                    parseTime(condition.startDateTime),
                  ),
                })
              }
            />
            <RedoTextInput
              inputTypeHint="time"
              placeholder="00:00"
              setValue={(time: string) =>
                onChange({
                  ...condition,
                  startDateTime: buildDateTime(
                    parseDate(condition.startDateTime)?.toString() ??
                      Temporal.Now.plainDateISO().toString(),
                    time,
                  ),
                })
              }
              size="sm"
              value={parseTime(condition.startDateTime)}
            />
          </Flex>
          <Text fontSize="sm" textColor="secondary">
            {t("and", { context: "conjunction between two range values" })}
          </Text>
          <Flex align="center" gap="sm">
            <RedoSingleDatePicker
              buttonSize="sm"
              date={parseDate(condition.endDateTime)}
              setDate={(date: Temporal.PlainDate) =>
                onChange({
                  ...condition,
                  endDateTime: buildDateTime(
                    date.toString(),
                    parseTime(condition.endDateTime),
                  ),
                })
              }
            />
            <RedoTextInput
              inputTypeHint="time"
              placeholder="00:00"
              setValue={(time: string) =>
                onChange({
                  ...condition,
                  endDateTime: buildDateTime(
                    parseDate(condition.endDateTime)?.toString() ??
                      Temporal.Now.plainDateISO().toString(),
                    time,
                  ),
                })
              }
              size="sm"
              value={parseTime(condition.endDateTime)}
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
